/**
 * Datetime handling for a tenant's timezone.
 *
 * `<input type="datetime-local">` has no zone. The browser reads and writes it as
 * *browser-local*, but a lead's time belongs to the *account's* zone -- a salon in
 * Chennai books at 10:00 IST whether or not whoever is entering it is sitting in
 * Chennai, or in Dubai, or in a browser whose clock is set to UTC.
 *
 * So every conversion here shifts by the account's offset rather than the
 * browser's. Getting it wrong is silent: the number saves, and it is the wrong
 * hour by half an hour.
 */

/** How far `timeZone` is from UTC, in ms, at that instant. */
function zoneOffsetMs(timeZone: string | undefined, at: Date): number {
  if (!timeZone) return 0;
  try {
    const parts = new Intl.DateTimeFormat("en-US", {
      timeZone,
      hour12: false,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    }).formatToParts(at);

    const get = (type: string) => Number(parts.find((p) => p.type === type)?.value);
    const asIfUtc = Date.UTC(
      get("year"),
      get("month") - 1,
      get("day"),
      // `hour` comes back as 24 at midnight in some engines; normalise it.
      get("hour") % 24,
      get("minute"),
    );
    return asIfUtc - at.getTime();
  } catch {
    // An unknown IANA name falls back to the browser's own reading rather than
    // refusing to save the time at all.
    return 0;
  }
}

/** An instant to the `YYYY-MM-DDTHH:mm` an input expects, in the account's zone. */
export function toLocalInput(
  iso: string | null | undefined,
  timeZone?: string,
): string {
  if (!iso) return "";
  const at = new Date(iso);
  if (Number.isNaN(at.getTime())) return "";
  const shifted = new Date(at.getTime() + zoneOffsetMs(timeZone, at));
  return shifted.toISOString().slice(0, 16);
}

/**
 * An input's value back to an instant.
 *
 * Read as UTC then shifted back by the zone offset, which is the inverse of
 * `toLocalInput`. `null` for an empty value, because "" is not a time and
 * posting it would clear a booking somebody made.
 */
export function fromLocalInput(
  value: string | null | undefined,
  timeZone?: string,
): string | null {
  const trimmed = value?.trim();
  if (!trimmed) return null;

  // `2026-10-05T09:30` read as UTC, then moved into the account's zone.
  const asUtc = new Date(`${trimmed.length === 16 ? `${trimmed}:00` : trimmed}Z`);
  if (Number.isNaN(asUtc.getTime())) return null;

  return new Date(asUtc.getTime() - zoneOffsetMs(timeZone, asUtc)).toISOString();
}

/** "09:30" from an instant, in the account's zone. */
export function timeOnly(iso: string | null | undefined, timeZone?: string): string {
  return toLocalInput(iso, timeZone).slice(11, 16);
}

/**
 * A clock time on a given day, as an instant.
 *
 * The correction form asks for the day once and the clock time for it, so the
 * two are joined here: "2026-10-05" and "09:30" mean 09:30 in the *account's*
 * zone, not in the browser's. `null` for a missing day or time -- there is no
 * punch to record, and "" would clear somebody's punch instead of leaving it.
 */
export function punchAt(
  date: string | null | undefined,
  time: string | null | undefined,
  timeZone?: string,
): string | null {
  const day = date?.trim();
  const clock = time?.trim();
  if (!day || !clock) return null;

  const match = /^(\d{1,2}):(\d{2})/.exec(clock);
  if (!match) return null;
  const hours = match[1]!.padStart(2, "0");
  const minutes = match[2]!;
  if (Number(hours) > 23 || Number(minutes) > 59) return null;

  return fromLocalInput(`${day}T${hours}:${minutes}:00`, timeZone);
}